import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useShowApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from 'utils/network/axios'
import { PagingResponse } from 'entities'
import { UserGroup, UserGroupForm, UserGroupSearchForm } from 'entities/user_group'

type UserGroupsResponse = PagingResponse & {
    results: UserGroup[]
}

export function useFetchUserGroupsApi(searchForm?: Form<UserGroupSearchForm>): IndexApiSet<UserGroupsResponse> & { execute: () => void } {
    const apiPath = 'user_groups/'
    const api = useIndexApi<UserGroupsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useFetchUserGroupApi(): ApiSet<BaseResponse> & { execute: (id: number) => void } {
    const apiSet = useShowApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number) => {
        const apiPath = `user_groups/${id}/`
        apiSet.execute(apiPath)
    }

    return { ...apiSet, execute: execute }
}

export function usePostUserGroupApi(): ApiSet<BaseResponse> & { execute: (form: Form<UserGroupForm>) => void } {
    const apiSet = usePostApi<BaseResponse, UserGroupForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<UserGroupForm>) => {
        const apiPath = `user_groups/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutUserGroupApi(): ApiSet<BaseResponse> & { execute: (form: Form<UserGroupForm>) => void } {
    const apiSet = usePutApi<BaseResponse, UserGroupForm>(new CraifHttpClient(), {
        initialResponse: {},
    }, {formatJson: true})

    const execute = (form: Form<UserGroupForm>) => {
        const apiPath = `user_groups/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutUserGroupsApi(): ApiSet<BaseResponse> & { execute: (form: Form<UserGroupForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, UserGroupForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<UserGroupForm[]>) => {
        const apiPath = `user_groups/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteUserGroupApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `user_groups/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
